import React, { forwardRef } from "react";
import { cn } from "../../styles";
import { BaseElementProps } from "../../props";
import { Typography, TypographySpanProps } from "../../atoms/Typography";
import { Link, LinkProps } from "../../atoms/Link";

export interface BreadcrumbsProps extends BaseElementProps {
  /**
   * The navigation breadcrumb items, should be an array of subcomponenets.
   */
  children: React.ReactNode;

  /**
   * The separator character/element to display between breadcrumb items.
   * @default "›"
   */
  separator?: React.ReactNode;

  /**
   * Aria label for the breadcrumbs navigation.
   */
  "aria-label"?: string;
}

/**
 * Container component for breadcrumb navigation.
 * Displays a horizontal list of navigation items separated by the specified separator.
 */
export const BreadcrumbsRoot = forwardRef<HTMLElement, BreadcrumbsProps>(
  (
    { className, separator = "›", children, "aria-label": ariaLabel, ...props },
    ref
  ) => {
    const childArray = React.Children.toArray(children);

    return (
      <nav
        ref={ref}
        className={cn("flex items-baseline gap-2", className)}
        aria-label={ariaLabel}
        {...props}
      >
        {childArray.map((child, index) => (
          <React.Fragment key={index}>
            {child}
            {index < childArray.length - 1 && (
              <Typography.Span className="text-sm">{separator}</Typography.Span>
            )}
          </React.Fragment>
        ))}
      </nav>
    );
  }
);

export interface BreadcrumbsLinkProps extends LinkProps {}

/**
 * Breadcrumb link component that can be navigated to.
 * Supports custom render functions for different router implementations.
 */
export const BreadcrumbsLink = Link;

export interface BreadcrumbsActiveProps extends TypographySpanProps {}

/**
 * Active breadcrumb component representing the current page.
 * Displayed without underline and in default text color.
 */
export const BreadcrumbsActive = forwardRef<
  HTMLSpanElement,
  BreadcrumbsActiveProps
>(({ className, ...typographyProps }, ref) => {
  return (
    <Typography.Span
      ref={ref}
      className={cn("text-sm", className)}
      {...typographyProps}
    />
  );
});

export interface BreadcrumbsPassiveProps extends TypographySpanProps {}

/**
 * Passive breadcrumb component for non-navigable items.
 * Displayed without underline and in muted gray color.
 */
export const BreadcrumbsPassive = forwardRef<
  HTMLSpanElement,
  BreadcrumbsPassiveProps
>(({ className, ...typographyProps }, ref) => {
  return (
    <Typography.Span
      ref={ref}
      className={cn("text-sm", className)}
      muted
      {...typographyProps}
    />
  );
});

const Breadcrumbs = Object.assign(BreadcrumbsRoot, {
  Link: BreadcrumbsLink,
  Active: BreadcrumbsActive,
  Passive: BreadcrumbsPassive,
});

Breadcrumbs.displayName = "Breadcrumbs";
Breadcrumbs.Link.displayName = "Breadcrumbs.Link";
Breadcrumbs.Active.displayName = "Breadcrumbs.Active";
Breadcrumbs.Passive.displayName = "Breadcrumbs.Passive";

export { Breadcrumbs };
